import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Divider,
  Drawer,
  Dropdown,
  Input,
  InputNumber,
  List,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DownOutlined,
  ExportOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { useIsMobile, useStoredBoolean } from '@/lib/hooks'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { FeedbackTriageTag } from '@/components/FeedbackTriageTag'
import { IpAddressLookup } from '@/components/IpAddressLookup'
import { ReplyTemplatePicker } from '@/components/ReplyTemplatePicker'
import { formatEpisodeLabel } from '@/lib/episode-feedback-triage'
import {
  formatSimilarity,
  parseDedupSuggestions,
  parseSimilarIssues,
  similarityBreakdown,
  similarityColor,
  suggestMergeKeep,
  suggestionKey,
  withoutIssue,
  withoutPair,
  type DedupIssueRef,
  type DedupSuggestion,
  type DedupSuggestionsResult,
  type SimilarIssue,
} from '@/lib/content-dedup'
import {
  TRIAGE_FILTER_OPTIONS,
  mergeSuccessMessage,
  parseEnvironmentRefs,
  parseTriage,
  triageFilterParam,
  withoutEnvironmentRefs,
  type EnvironmentRefs,
  type FeedbackTriage,
  type TriageLabel,
} from '@/lib/feedback-triage'

// ?issue=<uuid> 直接打开详情抽屉（剧集反馈转单成功后从那边链过来）
// ?view=dedup 打开「疑似重复」队列
const searchSchema = z.object({
  issue: z.string().uuid().optional().catch(undefined),
  view: z.enum(['issues', 'dedup']).optional().catch(undefined),
})

type PageView = 'issues' | 'dedup'

export const Route = createFileRoute('/_authed/product-feedback')({
  component: ProductFeedbackPage,
  validateSearch: searchSchema,
})

const NEXTJS_BASE_URL =
  import.meta.env.VITE_NEXTJS_BASE_URL || 'https://dm.xifanacg.com'

/* ── 类型（产品功能反馈；与剧集质量反馈队列隔离） ───────────── */

type PublicationTab = 'pending' | 'published' | 'hidden' | 'all'
type IssueType = 'bug' | 'experience' | 'feature_request' | 'other'
type IssueArea =
  | 'account'
  | 'discovery'
  | 'playback'
  | 'download'
  | 'danmaku'
  | 'notification'
  | 'ui_performance'
  | 'other'
type IssueStatus =
  | 'open'
  | 'investigating'
  | 'confirmed'
  | 'planned'
  | 'in_progress'
  | 'resolved'
  | 'not_planned'
  | 'closed'
  | 'merged'

interface IssueRow {
  id: string
  title: string
  status: IssueStatus
  publication_state: 'pending' | 'published' | 'hidden'
  issue_type: IssueType
  area: IssueArea | null
  version: number
  support_count: number
  occurrence_count: number
  created_by: string | null
  created_at: string
  last_activity_at: string
  /** AI 打标；还没建打标行时为 null */
  triage: FeedbackTriage | null
}

interface ListResult {
  ok: boolean
  total?: number
  rows?: Array<Omit<IssueRow, 'triage'> & { triage?: unknown }>
  error?: string
}

interface IssueDetail {
  id: string
  title: string
  normalized_title: string
  summary: string | null
  status: IssueStatus
  publication_state: 'pending' | 'published' | 'hidden'
  issue_type: IssueType
  area: IssueArea | null
  version: number
  merged_into_id: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  last_activity_at: string
  published_at: string | null
  terminal_at: string | null
}

interface IssueUpdate {
  kind: 'status_change' | 'official_note' | 'resolution'
  body: string | null
  status: string | null
  created_by: string | null
  created_at: string
}

interface IssueOccurrence {
  id: string
  user_id: string
  kind: string
  reported_title: string | null
  details: string | null
  environment: Record<string, unknown> | null
  client_kind: string | null
  /** 服务端抓取的请求 IP（2026-09-22 起写入，存量为 null） */
  ip_address: string | null
  /** 服务端抓取的请求 UA（可信；environment.ua 是客户端自报） */
  user_agent: string | null
  created_at: string
}

interface IssueAlias {
  id: number
  alias: string
  source: string
  review_state: string
  created_at: string
}

interface IssueSupporter {
  user_id: string
  client_kind: string | null
  created_at: string
}

interface DetailResult {
  ok: boolean
  issue?: IssueDetail
  updates?: Array<IssueUpdate>
  occurrences?: Array<IssueOccurrence>
  aliases?: Array<IssueAlias>
  supporters?: Array<IssueSupporter>
  /** 语义去重：与本问题最相似的其它问题（top 5，score >= 0.5；merged 问题为空） */
  similar_issues?: unknown
  error?: string
}

/** admin.get_user_overview 返回的子集——详情抽屉展示反馈人账号信息用 */
interface ReporterOverview {
  ok: boolean
  profile?: {
    username: string | null
    display_name: string | null
    status: string | null
  } | null
  auth?: {
    email: string | null
  } | null
  membership?: {
    vip_expiredate: string | null
    vip_is_permanent: boolean
    is_active_vip: boolean
  } | null
  error?: string
}

interface RpcResult {
  ok: boolean
  error?: string
  version?: number
  current_version?: number
  reason?: string
  field?: string
  supporters_before?: number
  supporters_after?: number
  /** 合并时收到通知的新并入用户数（目标隐藏时为 0） */
  notified?: number
}

/* ── i18n ───────────────────────────────────────────────────── */

const TYPE_LABELS: Record<IssueType, string> = {
  bug: 'Bug',
  experience: '体验',
  feature_request: '功能请求',
  other: '其他',
}

const TYPE_COLORS: Record<IssueType, string> = {
  bug: 'red',
  experience: 'orange',
  feature_request: 'blue',
  other: 'default',
}

const AREA_LABELS: Record<IssueArea, string> = {
  account: '账户',
  discovery: '发现',
  playback: '播放',
  download: '下载',
  danmaku: '弹幕',
  notification: '通知',
  ui_performance: '界面/性能',
  other: '其他',
}

const STATUS_LABELS: Record<IssueStatus, string> = {
  open: '开放',
  investigating: '调查中',
  confirmed: '已确认',
  planned: '已排期',
  in_progress: '进行中',
  resolved: '已解决',
  not_planned: '暂不计划',
  closed: '已关闭',
  merged: '已合并',
}

const PUB_LABELS: Record<string, string> = {
  pending: '待发布',
  published: '已发布',
  hidden: '已隐藏',
}

const PROFILE_STATUS_LABELS: Record<string, string> = {
  active: '正常',
  suspended: '暂停',
  banned: '已封禁',
}

const SETTABLE_STATUSES: Array<Exclude<IssueStatus, 'merged'>> = [
  'open',
  'investigating',
  'confirmed',
  'planned',
  'in_progress',
  'resolved',
  'not_planned',
  'closed',
]

function statusColor(s: IssueStatus): string {
  switch (s) {
    case 'open':
      return 'default'
    case 'investigating':
    case 'confirmed':
      return 'processing'
    case 'planned':
    case 'in_progress':
      return 'blue'
    case 'resolved':
      return 'success'
    case 'not_planned':
    case 'closed':
      return 'default'
    case 'merged':
      return 'purple'
    default:
      return 'default'
  }
}

function pubColor(s: string): string {
  switch (s) {
    case 'pending':
      return 'warning'
    case 'published':
      return 'success'
    case 'hidden':
      return 'default'
    default:
      return 'default'
  }
}

function reporterMembershipNode(m: ReporterOverview['membership']) {
  if (!m) return <Typography.Text type="secondary">无 VIP</Typography.Text>
  if (m.vip_is_permanent) return <Tag color="magenta">永久会员</Tag>
  if (m.is_active_vip) {
    return (
      <Tag color="gold">
        {m.vip_expiredate
          ? `VIP 至 ${formatCST(m.vip_expiredate, 'YYYY-MM-DD')}`
          : 'VIP 生效中'}
      </Tag>
    )
  }
  if (m.vip_expiredate) return <Tag>VIP 已过期</Tag>
  return <Typography.Text type="secondary">无 VIP</Typography.Text>
}

/* ── 设备环境（occurrences[].environment；web / android 字段形状不同） ── */

function envPart(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

/** environment jsonb → 单行摘要；未知字段形状兜底 key=value。ref_* 来源上下文另行展示。 */
function formatEnvironment(
  rawEnv: Record<string, unknown> | null,
): string | null {
  const env = withoutEnvironmentRefs(rawEnv)
  if (!env) return null
  const rawOs = envPart(env.os)
  const os = rawOs === 'android' ? 'Android' : rawOs === 'web' ? 'Web' : rawOs
  const osVersion = envPart(env.os_version)
  const sdkInt = envPart(env.sdk_int)
  const device = envPart(env.device)
  const appVersion = envPart(env.app_version)
  const appBuild = envPart(env.app_build)
  const app = envPart(env.app)

  const parts: string[] = []
  if (os) parts.push(osVersion ? `${os} ${osVersion}` : os)
  if (sdkInt) parts.push(`SDK ${sdkInt}`)
  if (device) parts.push(device)
  if (appVersion)
    parts.push(`v${appVersion}${appBuild ? ` (${appBuild})` : ''}`)
  if (app) parts.push(app)
  if (parts.length > 0) return parts.join(' · ')

  const generic = Object.entries(env)
    .map(([k, v]) => {
      const s = envPart(v)
      return s ? `${k}=${s}` : null
    })
    .filter((s): s is string => s !== null)
  return generic.length > 0 ? generic.join(' · ') : null
}

function envUserAgent(env: Record<string, unknown> | null): string | null {
  return env ? envPart(env.ua) : null
}

function rpcErrorMessage(r: RpcResult): string {
  if (r.error === 'version_conflict') {
    return `版本冲突（当前 v${r.current_version ?? '?'}），请刷新后重试`
  }
  if (r.error === 'invalid_request') {
    if (r.reason === 'merged_issues_cannot_change_status') {
      return '已合并的问题不能改状态'
    }
    if (r.reason === 'cannot_merge_into_self') return '不能合并到自身'
    if (r.reason === 'source_already_merged') return '源问题已合并'
    if (r.reason === 'target_is_merged') return '目标问题已合并'
    if (r.field) return `参数无效：${r.field}`
    return r.reason ?? '请求无效'
  }
  return r.error ?? 'unknown'
}

async function adminRpc(
  name: string,
  args: Record<string, unknown>,
): Promise<RpcResult> {
  const { data, error } = await (supabase as any)
    .schema('admin')
    .rpc(name, args)
  if (error) throw error
  const r = data as RpcResult
  if (!r?.ok)
    throw new Error(rpcErrorMessage(r ?? { ok: false, error: 'unknown' }))
  return r
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

const PAGE_SIZE = 50

/* ── 主组件 ─────────────────────────────────────────────────── */

function ProductFeedbackPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const search$ = Route.useSearch()
  const navigate = Route.useNavigate()
  const isMobile = useIsMobile()

  const view: PageView = search$.view ?? 'issues'
  const setView = (next: PageView) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        view: next === 'issues' ? undefined : next,
      }),
      replace: true,
    })
  }
  const [tab, setTab] = useState<PublicationTab>('pending')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<IssueStatus | undefined>()
  const [issueType, setIssueType] = useState<IssueType | undefined>()
  const [area, setArea] = useState<IssueArea | undefined>()
  const [search, setSearch] = useState('')
  const [memberOnly, setMemberOnly] = useStoredBoolean(
    'xfdm-admin:product-feedback:member-only',
  )
  const [hideContentRequests, setHideContentRequests] = useStoredBoolean(
    'xfdm-admin:product-feedback:hide-content-requests',
  )
  const [triageLabel, setTriageLabel] = useState<TriageLabel | undefined>()
  const debouncedSearch = useDebounced(search.trim(), 300)

  const [detailId, setDetailIdState] = useState<string | null>(
    search$.issue ?? null,
  )
  // 抽屉开关同步到 URL，便于从别处深链；关掉时清掉参数
  const setDetailId = (id: string | null) => {
    setDetailIdState(id)
    void navigate({
      search: (prev) => ({ ...prev, issue: id ?? undefined }),
      replace: true,
    })
  }

  useEffect(() => {
    setPage(1)
  }, [
    tab,
    status,
    issueType,
    area,
    memberOnly,
    hideContentRequests,
    triageLabel,
    debouncedSearch,
  ])

  const listQuery = useQuery({
    queryKey: [
      'product_issues',
      {
        tab,
        page,
        status,
        issueType,
        area,
        memberOnly,
        hideContentRequests,
        triageLabel,
        search: debouncedSearch,
      },
    ],
    placeholderData: keepPreviousData,
    enabled: view === 'issues',
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_product_issues', {
          p_status: status ?? null,
          p_publication_state: tab === 'all' ? null : tab,
          p_issue_type: issueType ?? null,
          p_area: area ?? null,
          p_search: debouncedSearch || null,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
          p_member_only: memberOnly,
          p_triage_label: triageFilterParam(triageLabel),
          // 只在打开时传，旧后端（未上 exclude 参数）下开关关闭仍可用
          ...(hideContentRequests
            ? { p_exclude_triage_label: 'content_request' }
            : {}),
        })
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      const rows: Array<IssueRow> = (r.rows ?? []).map((row) => ({
        ...row,
        triage: parseTriage(row.triage),
      }))
      return { rows, total: r.total ?? 0 }
    },
  })

  const invalidateTriage = () => {
    qc.invalidateQueries({ queryKey: ['product_issues'] })
  }

  const columns: ColumnsType<IssueRow> = useMemo(
    () => [
      {
        title: '标题',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 13 }}>
              {row.title}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{
                fontSize: 11,
                fontFamily: 'var(--ant-font-family-code, monospace)',
              }}
              copyable={{ text: row.id }}
            >
              {row.id.slice(0, 8)}…
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: (
          <Tooltip title="AI 自动打标，人工修改后以人工为准。悬停看 AI 理由与置信度">
            标签
          </Tooltip>
        ),
        width: 170,
        render: (_: unknown, row) => (
          <FeedbackTriageTag
            triage={row.triage}
            sourceKind="product_issue"
            publicId={row.id}
            onChanged={invalidateTriage}
          />
        ),
      },
      {
        title: '类型',
        width: 110,
        render: (_: unknown, row) => (
          <Tag color={TYPE_COLORS[row.issue_type]}>
            {TYPE_LABELS[row.issue_type]}
          </Tag>
        ),
      },
      {
        title: '区域',
        width: 100,
        render: (_: unknown, row) =>
          row.area ? (
            <Tag>{AREA_LABELS[row.area]}</Tag>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          ),
      },
      {
        title: '状态',
        width: 100,
        render: (_: unknown, row) => (
          <Tag color={statusColor(row.status)}>{STATUS_LABELS[row.status]}</Tag>
        ),
      },
      {
        title: '可见性',
        width: 90,
        render: (_: unknown, row) => (
          <Tag color={pubColor(row.publication_state)}>
            {PUB_LABELS[row.publication_state]}
          </Tag>
        ),
      },
      {
        title: '支持 / 复现',
        width: 100,
        render: (_: unknown, row) => (
          <Typography.Text
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {row.support_count} / {row.occurrence_count}
          </Typography.Text>
        ),
      },
      {
        title: '最近活动',
        width: 130,
        render: (_: unknown, row) => (
          <Typography.Text
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCST(row.last_activity_at, 'MM-DD HH:mm')}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 90,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button type="link" size="small" onClick={() => setDetailId(row.id)}>
            {row.publication_state === 'pending' ? '受理' : '查看'}
          </Button>
        ),
      },
    ],
    [],
  )

  const rows = listQuery.data?.rows ?? []
  const total = listQuery.data?.total ?? 0
  const detailTriage = rows.find((row) => row.id === detailId)?.triage

  const viewSwitch = (
    <Segmented<PageView>
      value={view}
      onChange={setView}
      options={[
        { label: '问题列表', value: 'issues' },
        { label: '疑似重复', value: 'dedup' },
      ]}
    />
  )

  const drawer = (
    <IssueDrawer
      issueId={detailId}
      onClose={() => setDetailId(null)}
      onOpenIssue={setDetailId}
      triage={detailTriage}
      onChanged={() => {
        qc.invalidateQueries({ queryKey: ['product_issues'] })
        qc.invalidateQueries({ queryKey: ['product_issue', detailId] })
      }}
      message={message}
    />
  )

  if (view === 'dedup') {
    return (
      <>
        <DedupQueue viewSwitch={viewSwitch} onOpenIssue={setDetailId} />
        {drawer}
      </>
    )
  }

  return (
    <>
      <Card
        title="产品反馈"
        extra={
          <Space size={8} wrap>
            {viewSwitch}
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="功能 / 体验反馈队列（private.product_issues）。与「剧集反馈」完全隔离，勿混用。"
          />

          <Segmented<PublicationTab>
            value={tab}
            onChange={(v) => setTab(v)}
            options={[
              { label: '待发布', value: 'pending' },
              { label: '已发布', value: 'published' },
              { label: '已隐藏', value: 'hidden' },
              { label: '全部', value: 'all' },
            ]}
          />

          <AdminFilterBar
            activeCount={
              (status ? 1 : 0) +
              (issueType ? 1 : 0) +
              (area ? 1 : 0) +
              (triageLabel ? 1 : 0) +
              (memberOnly ? 1 : 0) +
              (hideContentRequests ? 1 : 0)
            }
            primary={
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="搜标题"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 240 }}
              />
            }
          >
            <Select
              allowClear
              placeholder="状态"
              style={{ width: 140 }}
              value={status}
              onClear={() => setStatus(undefined)}
              onChange={(v) => setStatus(v)}
              options={(
                Object.entries(STATUS_LABELS) as Array<[IssueStatus, string]>
              ).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="类型"
              style={{ width: 140 }}
              value={issueType}
              onClear={() => setIssueType(undefined)}
              onChange={(v) => setIssueType(v)}
              options={(
                Object.entries(TYPE_LABELS) as Array<[IssueType, string]>
              ).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="区域"
              style={{ width: 140 }}
              value={area}
              onClear={() => setArea(undefined)}
              onChange={(v) => setArea(v)}
              options={(
                Object.entries(AREA_LABELS) as Array<[IssueArea, string]>
              ).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="标签"
              aria-label="按标签筛选"
              style={{ width: 140 }}
              value={triageLabel}
              onClear={() => setTriageLabel(undefined)}
              onChange={(v) => setTriageLabel(v)}
              options={TRIAGE_FILTER_OPTIONS}
            />
            {/* 优先处理：仅看有当前会员提交过的反馈 */}
            <Space size={6} className="admin-filter-grid__full">
              <span style={{ fontSize: 13 }}>会员优先</span>
              <Switch
                checked={memberOnly}
                onChange={setMemberOnly}
                size="small"
                aria-label="会员优先"
              />
            </Space>
            {/* 资源请求（求番 / 补档）另行批量处理，日常巡检时可隐藏 */}
            <Space size={6} className="admin-filter-grid__full">
              <span style={{ fontSize: 13 }}>忽略资源请求</span>
              <Switch
                checked={hideContentRequests}
                onChange={setHideContentRequests}
                size="small"
                aria-label="忽略资源请求"
              />
            </Space>
          </AdminFilterBar>

          <div className="admin-table-wrap">
            <Table<IssueRow>
              rowKey="id"
              size="middle"
              loading={listQuery.isLoading}
              columns={columns}
              dataSource={rows}
              scroll={{ x: 1270 }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                showTotal: (totalRows) => `共 ${totalRows} 条`,
                onChange: (p) => setPage(p),
              }}
            />
          </div>

          <AdminMobileList
            rows={rows}
            rowKey={(row) => row.id}
            loading={listQuery.isLoading || listQuery.isFetching}
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            renderItem={(row) => (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                aria-label={`${row.publication_state === 'pending' ? '受理' : '查看'}：${row.title}`}
                onClick={() => setDetailId(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setDetailId(row.id)
                  }
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title">
                    <Typography.Text>{row.title}</Typography.Text>
                    <div className="admin-mobile-record__meta">
                      <span>
                        支持 {row.support_count} · 复现 {row.occurrence_count}
                      </span>
                    </div>
                  </div>
                  <Tag color={statusColor(row.status)}>
                    {STATUS_LABELS[row.status]}
                  </Tag>
                </div>
                <div className="admin-mobile-record__badges">
                  <Tag color={TYPE_COLORS[row.issue_type]}>
                    {TYPE_LABELS[row.issue_type]}
                  </Tag>
                  <Tag color={pubColor(row.publication_state)}>
                    {PUB_LABELS[row.publication_state]}
                  </Tag>
                  {row.area ? <Tag>{AREA_LABELS[row.area]}</Tag> : null}
                  <FeedbackTriageTag
                    triage={row.triage}
                    sourceKind="product_issue"
                    publicId={row.id}
                    onChanged={invalidateTriage}
                  />
                </div>
                <div className="admin-mobile-record__meta">
                  <span>
                    最近活动 {formatCST(row.last_activity_at, 'MM-DD HH:mm')}
                  </span>
                </div>
              </article>
            )}
          />
        </Space>
      </Card>

      {drawer}
    </>
  )
}

/* ── 合并（详情抽屉 / 相似问题 / 疑似重复队列共用） ───────────── */

interface MergeIssueRef {
  id: string
  title?: string | null
}

const DEDUP_QUEUE_KEY = 'product_issue_dedup_suggestions'

/**
 * merge_product_issues + 统一确认框。source 并入 target（source 变 merged，不可撤销）。
 * onMerged 只在成功后调用；失败时确认框保持打开并提示错误。
 */
function useProductIssueMerge() {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()

  const mergeMutation = useMutation({
    mutationFn: async (v: { sourceId: string; targetId: string }) =>
      adminRpc('merge_product_issues', {
        p_source_id: v.sourceId,
        p_target_id: v.targetId,
      }),
    onSuccess: (r, v) => {
      message.success(mergeSuccessMessage(r))
      qc.invalidateQueries({ queryKey: ['product_issues'] })
      qc.invalidateQueries({ queryKey: ['product_issue', v.sourceId] })
      qc.invalidateQueries({ queryKey: ['product_issue', v.targetId] })
      qc.invalidateQueries({ queryKey: [DEDUP_QUEUE_KEY] })
    },
    onError: (e: Error) => message.error(e.message || '合并失败'),
  })

  const confirmMerge = (
    source: MergeIssueRef,
    target: MergeIssueRef,
    onMerged?: () => void,
  ) => {
    const describe = (ref: MergeIssueRef) =>
      ref.title ? `「${ref.title}」 ${ref.id}` : ref.id
    modal.confirm({
      title: '合并到另一条问题',
      content: (
        <Space direction="vertical" size={8}>
          <Typography.Text>
            将把源问题合并进目标，并迁移支持者 / 复现 / 别名。此操作不可撤销。
          </Typography.Text>
          <Typography.Text>
            合并后，新并入目标的用户会各收到一条站内通知，正文包含目标的当前状态和最近一条公开回复（目标已隐藏时不通知）。
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            源（将被合并）：{describe(source)}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            目标（保留）：{describe(target)}
          </Typography.Text>
        </Space>
      ),
      okText: '确认合并',
      okButtonProps: { danger: true },
      onOk: async () => {
        await mergeMutation.mutateAsync({
          sourceId: source.id,
          targetId: target.id,
        })
        onMerged?.()
      },
    })
  }

  return { confirmMerge, isPending: mergeMutation.isPending }
}

/* ── 详情抽屉 ───────────────────────────────────────────────── */

function IssueDrawer({
  issueId,
  onClose,
  onOpenIssue,
  triage,
  onChanged,
  message,
}: {
  issueId: string | null
  onClose: () => void
  onOpenIssue: (id: string) => void
  /** 来自当前列表页；不在当前页（如从合并目标跳转）时为 undefined */
  triage: FeedbackTriage | null | undefined
  onChanged: () => void
  message: ReturnType<typeof AntdApp.useApp>['message']
}) {
  const isMobile = useIsMobile()
  const [editTitle, setEditTitle] = useState('')
  const [editSummary, setEditSummary] = useState('')
  const [nextStatus, setNextStatus] = useState<
    Exclude<IssueStatus, 'merged'> | undefined
  >()
  const [statusNote, setStatusNote] = useState('')
  const [updateKind, setUpdateKind] = useState<'official_note' | 'resolution'>(
    'official_note',
  )
  const [updateBody, setUpdateBody] = useState('')
  const [aliasText, setAliasText] = useState('')
  const [mergeTargetId, setMergeTargetId] = useState('')

  const detailQuery = useQuery({
    queryKey: ['product_issue', issueId],
    enabled: !!issueId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_product_issue', { p_issue_id: issueId })
      if (error) throw error
      const r = data as DetailResult
      if (!r.ok || !r.issue) throw new Error(r.error ?? 'not_found')
      return r
    },
  })

  const issue = detailQuery.data?.issue
  const updates = detailQuery.data?.updates ?? []
  const occurrences = detailQuery.data?.occurrences ?? []
  const aliases = detailQuery.data?.aliases ?? []
  const supporters = detailQuery.data?.supporters ?? []

  const reporterUid = issue?.created_by ?? null
  const reporterQuery = useQuery({
    queryKey: ['user_overview', reporterUid],
    enabled: !!reporterUid,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_user_overview', { p_target_user_id: reporterUid })
      if (error) throw error
      const r = data as ReporterOverview
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
  })
  const reporter = reporterQuery.data

  // occurrence.environment 里的 ref_*（用户从播放页跳过来反馈时带的来源）
  const refsByOccurrence = useMemo(() => {
    const map = new Map<string, EnvironmentRefs>()
    for (const o of occurrences) {
      const refs = parseEnvironmentRefs(o.environment)
      if (refs) map.set(o.id, refs)
    }
    return map
  }, [occurrences])
  const sourceLookup = useSourceLookup([...refsByOccurrence.values()])

  // 反馈人首发提交：优先 initial occurrence，否则取本人最早一条（occurrences 按时间倒序）
  const reporterOccurrence = useMemo(() => {
    const uid = issue?.created_by
    if (!uid) return null
    const own = occurrences.filter((o) => o.user_id === uid)
    if (own.length === 0) return null
    return own.find((o) => o.kind === 'initial') ?? own[own.length - 1]
  }, [issue?.created_by, occurrences])

  useEffect(() => {
    if (!issue) return
    setEditTitle(issue.title)
    setEditSummary(issue.summary ?? '')
    setNextStatus(
      issue.status === 'merged'
        ? undefined
        : (issue.status as Exclude<IssueStatus, 'merged'>),
    )
    setStatusNote('')
    setUpdateBody('')
    setAliasText('')
    setMergeTargetId('')
  }, [issue?.id, issue?.version])

  const invalidateAndNotify = (msg: string) => {
    message.success(msg)
    onChanged()
  }

  const publishMutation = useMutation({
    mutationFn: async (publication_state: 'published' | 'hidden') => {
      if (!issue) throw new Error('no_issue')
      return adminRpc('publish_product_issue', {
        p_issue_id: issue.id,
        p_title: editTitle.trim() || null,
        p_summary: editSummary.trim() || null,
        p_publication_state: publication_state,
        p_expected_version: issue.version,
      })
    },
    onSuccess: (_r, publication_state) => {
      invalidateAndNotify(
        publication_state === 'published' ? '已发布' : '已隐藏',
      )
    },
    onError: (e: Error) => message.error(e.message || '操作失败'),
  })

  const statusMutation = useMutation({
    mutationFn: async () => {
      if (!issue || !nextStatus) throw new Error('no_status')
      return adminRpc('set_product_issue_status', {
        p_issue_id: issue.id,
        p_status: nextStatus,
        p_note: statusNote.trim() || null,
        p_expected_version: issue.version,
      })
    },
    onSuccess: () => {
      invalidateAndNotify('状态已更新')
      setStatusNote('')
    },
    onError: (e: Error) => message.error(e.message || '状态更新失败'),
  })

  const noteMutation = useMutation({
    mutationFn: async () => {
      if (!issue) throw new Error('no_issue')
      const body = updateBody.trim()
      if (!body) throw new Error('正文不能为空')
      return adminRpc('post_product_issue_update', {
        p_issue_id: issue.id,
        p_kind: updateKind,
        p_body: body,
      })
    },
    onSuccess: () => {
      invalidateAndNotify('已发布更新')
      setUpdateBody('')
    },
    onError: (e: Error) => message.error(e.message || '发布失败'),
  })

  const aliasMutation = useMutation({
    mutationFn: async () => {
      if (!issue) throw new Error('no_issue')
      const alias = aliasText.trim()
      if (!alias) throw new Error('别名不能为空')
      return adminRpc('add_product_issue_alias', {
        p_issue_id: issue.id,
        p_alias: alias,
      })
    },
    onSuccess: () => {
      invalidateAndNotify('别名已添加')
      setAliasText('')
    },
    onError: (e: Error) => message.error(e.message || '添加失败'),
  })

  const merge = useProductIssueMerge()
  const similarIssues = useMemo(
    () => parseSimilarIssues(detailQuery.data?.similar_issues),
    [detailQuery.data?.similar_issues],
  )

  const confirmMerge = () => {
    const target = mergeTargetId.trim()
    if (!target) {
      message.error('请填写目标问题 UUID')
      return
    }
    if (!issue) return
    merge.confirmMerge(
      { id: issue.id, title: issue.title },
      { id: target },
      onClose,
    )
  }

  const isMerged = issue?.status === 'merged'
  const busy =
    publishMutation.isPending ||
    statusMutation.isPending ||
    noteMutation.isPending ||
    aliasMutation.isPending ||
    merge.isPending

  return (
    <Drawer
      open={!!issueId}
      onClose={onClose}
      width={isMobile ? '100%' : 720}
      title={
        issue
          ? `产品反馈 · ${issue.publication_state === 'pending' ? '受理' : '详情'}`
          : '产品反馈'
      }
      destroyOnHidden
    >
      {detailQuery.isLoading ? (
        <Typography.Text type="secondary">加载中…</Typography.Text>
      ) : detailQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message={(detailQuery.error as Error).message || '加载失败'}
        />
      ) : !issue ? null : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {isMerged ? (
            <Alert
              type="info"
              showIcon
              message="该问题已合并"
              description={
                issue.merged_into_id ? (
                  <Space size={8} wrap>
                    <Typography.Text
                      copyable={{ text: issue.merged_into_id }}
                      style={{
                        fontSize: 12,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      合并目标：{issue.merged_into_id}
                    </Typography.Text>
                    <Button
                      size="small"
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() => onOpenIssue(issue.merged_into_id!)}
                    >
                      查看目标 →
                    </Button>
                  </Space>
                ) : undefined
              }
            />
          ) : null}

          <Descriptions
            column={1}
            size="small"
            bordered
            layout={isMobile ? 'vertical' : 'horizontal'}
            items={[
              {
                label: '标题',
                children: issue.title,
              },
              {
                label: '摘要',
                children: issue.summary ? (
                  <span style={{ whiteSpace: 'pre-wrap' }}>
                    {issue.summary}
                  </span>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
              },
              {
                label: '类型 / 区域',
                children: (
                  <Space size={4} wrap>
                    <Tag color={TYPE_COLORS[issue.issue_type]}>
                      {TYPE_LABELS[issue.issue_type]}
                    </Tag>
                    {issue.area ? (
                      <Tag>{AREA_LABELS[issue.area]}</Tag>
                    ) : (
                      <Typography.Text type="secondary">无区域</Typography.Text>
                    )}
                  </Space>
                ),
              },
              ...(triage !== undefined
                ? [
                    {
                      label: '标签',
                      children: (
                        <Space direction="vertical" size={2}>
                          <FeedbackTriageTag
                            triage={triage}
                            sourceKind="product_issue"
                            publicId={issue.id}
                            emptyText="尚未打标"
                            onChanged={onChanged}
                          />
                          {triage?.reason ? (
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 12 }}
                            >
                              AI 理由：{triage.reason}
                            </Typography.Text>
                          ) : null}
                        </Space>
                      ),
                    },
                  ]
                : []),
              {
                label: '状态 / 可见性',
                children: (
                  <Space size={4} wrap>
                    <Tag color={statusColor(issue.status)}>
                      {STATUS_LABELS[issue.status]}
                    </Tag>
                    <Tag color={pubColor(issue.publication_state)}>
                      {PUB_LABELS[issue.publication_state]}
                    </Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      v{issue.version}
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                label: '支持 / 复现',
                children: `${supporters.length} / ${occurrences.length}`,
              },
              {
                label: '反馈人',
                children: !issue.created_by ? (
                  <Typography.Text type="secondary">—</Typography.Text>
                ) : reporterQuery.isLoading ? (
                  <Typography.Text type="secondary">加载中…</Typography.Text>
                ) : reporterQuery.isError ? (
                  <Space size={6} wrap>
                    <Typography.Text
                      copyable={{ text: issue.created_by }}
                      style={{
                        fontSize: 12,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      {issue.created_by.slice(0, 8)}…
                    </Typography.Text>
                    <Typography.Text type="danger" style={{ fontSize: 12 }}>
                      资料加载失败
                    </Typography.Text>
                  </Space>
                ) : (
                  <Space direction="vertical" size={2}>
                    <Space size={6} wrap>
                      <Typography.Text strong>
                        {reporter?.profile?.display_name ||
                          reporter?.profile?.username ||
                          '未设置用户名'}
                      </Typography.Text>
                      {reporter?.profile?.display_name &&
                      reporter?.profile?.username &&
                      reporter.profile.display_name !==
                        reporter.profile.username ? (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          @{reporter.profile.username}
                        </Typography.Text>
                      ) : null}
                      {reporter?.profile?.status &&
                      reporter.profile.status !== 'active' ? (
                        <Tag
                          color={
                            reporter.profile.status === 'banned'
                              ? 'red'
                              : 'orange'
                          }
                        >
                          {PROFILE_STATUS_LABELS[reporter.profile.status] ??
                            reporter.profile.status}
                        </Tag>
                      ) : null}
                      {reporterMembershipNode(reporter?.membership)}
                    </Space>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {reporter?.auth?.email ?? '无邮箱'}
                    </Typography.Text>
                    {reporterOccurrence?.environment ? (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {formatEnvironment(reporterOccurrence.environment)}
                      </Typography.Text>
                    ) : null}
                    {reporterOccurrence?.ip_address ? (
                      <IpAddressLookup ip={reporterOccurrence.ip_address} />
                    ) : null}
                    <Typography.Text
                      type="secondary"
                      copyable={{ text: issue.created_by }}
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--ant-font-family-code, monospace)',
                      }}
                    >
                      {issue.created_by}
                    </Typography.Text>
                  </Space>
                ),
              },
              {
                label: 'ID',
                children: (
                  <Typography.Text
                    copyable
                    style={{
                      fontSize: 12,
                      fontFamily: 'var(--ant-font-family-code, monospace)',
                    }}
                  >
                    {issue.id}
                  </Typography.Text>
                ),
              },
              {
                label: '时间',
                children: (
                  <Typography.Text style={{ fontSize: 12 }}>
                    创建 {formatCST(issue.created_at)} · 活动{' '}
                    {formatCST(issue.last_activity_at)}
                    {issue.published_at
                      ? ` · 发布 ${formatCST(issue.published_at)}`
                      : ''}
                    {issue.terminal_at
                      ? ` · 终态 ${formatCST(issue.terminal_at)}`
                      : ''}
                  </Typography.Text>
                ),
              },
            ]}
          />

          {!isMerged ? (
            <SimilarIssuesPanel
              items={similarIssues}
              disabled={busy}
              onOpen={onOpenIssue}
              onMergeInto={(item) =>
                // 当前问题并入相似问题：当前变 merged，跳到保留下来的那条
                merge.confirmMerge(
                  { id: issue.id, title: issue.title },
                  { id: item.id, title: item.title },
                  () => onOpenIssue(item.id),
                )
              }
              onMergeHere={(item) =>
                // 相似问题并入当前：留在当前详情，invalidate 后自动刷新
                merge.confirmMerge(
                  { id: item.id, title: item.title },
                  { id: issue.id, title: issue.title },
                )
              }
            />
          ) : null}

          {!isMerged ? (
            <>
              <Divider titlePlacement="start" plain>
                发布 / 编辑
              </Divider>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="标题"
                  disabled={busy}
                />
                <Input.TextArea
                  value={editSummary}
                  onChange={(e) => setEditSummary(e.target.value)}
                  placeholder="公开摘要（可选）"
                  rows={3}
                  disabled={busy}
                />
                <Space wrap>
                  <Button
                    type="primary"
                    loading={publishMutation.isPending}
                    disabled={busy}
                    onClick={() => publishMutation.mutate('published')}
                  >
                    {issue.publication_state === 'published'
                      ? '保存并保持发布'
                      : '发布'}
                  </Button>
                  <Button
                    loading={publishMutation.isPending}
                    disabled={busy}
                    onClick={() => publishMutation.mutate('hidden')}
                  >
                    隐藏
                  </Button>
                </Space>
              </Space>

              <Divider titlePlacement="start" plain>
                改状态
              </Divider>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Select
                  style={{ width: '100%' }}
                  value={nextStatus}
                  onChange={(v) => setNextStatus(v)}
                  disabled={busy}
                  options={SETTABLE_STATUSES.map((value) => ({
                    value,
                    label: STATUS_LABELS[value],
                  }))}
                />
                <ReplyTemplatePicker
                  context="product_issue"
                  actions={nextStatus ? [nextStatus] : []}
                  facet={issue.issue_type}
                  value={statusNote}
                  onChange={setStatusNote}
                  disabled={busy}
                />
                <Input.TextArea
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="状态说明（进入终态时会通知支持者）"
                  rows={2}
                  disabled={busy}
                />
                <Button
                  type="primary"
                  loading={statusMutation.isPending}
                  disabled={busy || !nextStatus || nextStatus === issue.status}
                  onClick={() => statusMutation.mutate()}
                >
                  更新状态
                </Button>
              </Space>

              <Divider titlePlacement="start" plain>
                官方更新
              </Divider>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Select
                  style={{ width: 180 }}
                  value={updateKind}
                  onChange={(v) => setUpdateKind(v)}
                  disabled={busy}
                  options={[
                    { value: 'official_note', label: '官方说明' },
                    { value: 'resolution', label: '结论' },
                  ]}
                />
                <ReplyTemplatePicker
                  context="product_issue"
                  actions={[updateKind]}
                  facet={issue.issue_type}
                  value={updateBody}
                  onChange={setUpdateBody}
                  disabled={busy}
                />
                <Input.TextArea
                  value={updateBody}
                  onChange={(e) => setUpdateBody(e.target.value)}
                  placeholder="更新正文"
                  rows={3}
                  disabled={busy}
                />
                <Button
                  loading={noteMutation.isPending}
                  disabled={busy || !updateBody.trim()}
                  onClick={() => noteMutation.mutate()}
                >
                  发布更新
                </Button>
              </Space>

              <Divider titlePlacement="start" plain>
                别名
              </Divider>
              <Space.Compact
                className="product-feedback-alias"
                style={{ width: '100%' }}
              >
                <Input
                  value={aliasText}
                  onChange={(e) => setAliasText(e.target.value)}
                  placeholder="用于模糊匹配的别名"
                  disabled={busy}
                />
                <Button
                  loading={aliasMutation.isPending}
                  disabled={busy || !aliasText.trim()}
                  onClick={() => aliasMutation.mutate()}
                >
                  添加
                </Button>
              </Space.Compact>

              <Divider titlePlacement="start" plain>
                合并
              </Divider>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Input
                  value={mergeTargetId}
                  onChange={(e) => setMergeTargetId(e.target.value)}
                  placeholder="目标问题 UUID"
                  disabled={busy}
                />
                <Button
                  danger
                  loading={merge.isPending}
                  disabled={busy || !mergeTargetId.trim()}
                  onClick={confirmMerge}
                >
                  合并到目标
                </Button>
              </Space>
            </>
          ) : null}

          <Divider titlePlacement="start" plain>
            时间线
          </Divider>
          {updates.length === 0 ? (
            <Typography.Text type="secondary">暂无更新</Typography.Text>
          ) : (
            <Timeline
              items={updates.map((u, i) => ({
                key: `${u.created_at}-${i}`,
                children: (
                  <Space direction="vertical" size={0}>
                    <Space size={6} wrap>
                      <Tag>
                        {u.kind === 'status_change'
                          ? '状态变更'
                          : u.kind === 'resolution'
                            ? '结论'
                            : '官方说明'}
                      </Tag>
                      {u.status ? (
                        <Tag color={statusColor(u.status as IssueStatus)}>
                          {STATUS_LABELS[u.status as IssueStatus] ?? u.status}
                        </Tag>
                      ) : null}
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 11 }}
                      >
                        {formatCST(u.created_at, 'MM-DD HH:mm')}
                      </Typography.Text>
                    </Space>
                    {u.body ? (
                      <Typography.Paragraph
                        style={{
                          marginBottom: 0,
                          fontSize: 13,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {u.body}
                      </Typography.Paragraph>
                    ) : null}
                  </Space>
                ),
              }))}
            />
          )}

          <Divider titlePlacement="start" plain>
            复现记录 ({occurrences.length})
          </Divider>
          {occurrences.length === 0 ? (
            <Typography.Text type="secondary">暂无复现</Typography.Text>
          ) : (
            <List
              size="small"
              dataSource={occurrences}
              renderItem={(o) => {
                const envText = formatEnvironment(o.environment)
                // 服务端抓取的 UA 优先；存量行只有客户端自报的 environment.ua
                const ua = o.user_agent ?? envUserAgent(o.environment)
                return (
                  <List.Item>
                    <Space
                      direction="vertical"
                      size={2}
                      style={{ width: '100%' }}
                    >
                      <Space wrap size={6}>
                        <Tag>{o.kind}</Tag>
                        {o.client_kind ? <Tag>{o.client_kind}</Tag> : null}
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 11 }}
                        >
                          {formatCST(o.created_at, 'MM-DD HH:mm')}
                        </Typography.Text>
                        <Typography.Text
                          type="secondary"
                          copyable={{ text: o.user_id }}
                          style={{
                            fontSize: 11,
                            fontFamily:
                              'var(--ant-font-family-code, monospace)',
                          }}
                        >
                          {o.user_id.slice(0, 8)}…
                        </Typography.Text>
                      </Space>
                      {o.reported_title ? (
                        <Typography.Text style={{ fontSize: 12 }}>
                          上报标题：{o.reported_title}
                        </Typography.Text>
                      ) : null}
                      {o.details ? (
                        <Typography.Paragraph
                          ellipsis={{ rows: 3, expandable: true }}
                          style={{
                            marginBottom: 0,
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {o.details}
                        </Typography.Paragraph>
                      ) : null}
                      {refsByOccurrence.get(o.id) ? (
                        <SourceRefsLine
                          refs={refsByOccurrence.get(o.id)!}
                          lookup={sourceLookup}
                        />
                      ) : null}
                      {envText ? (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 11 }}
                        >
                          {envText}
                        </Typography.Text>
                      ) : null}
                      {o.ip_address ? (
                        <IpAddressLookup ip={o.ip_address} />
                      ) : null}
                      {ua ? (
                        <Typography.Paragraph
                          type="secondary"
                          copyable={{ text: ua }}
                          ellipsis={{
                            rows: 1,
                            expandable: true,
                            symbol: '展开',
                          }}
                          style={{
                            marginBottom: 0,
                            fontSize: 11,
                            fontFamily:
                              'var(--ant-font-family-code, monospace)',
                          }}
                        >
                          {ua}
                        </Typography.Paragraph>
                      ) : null}
                    </Space>
                  </List.Item>
                )
              }}
            />
          )}

          <Divider titlePlacement="start" plain>
            别名 ({aliases.length})
          </Divider>
          {aliases.length === 0 ? (
            <Typography.Text type="secondary">暂无别名</Typography.Text>
          ) : (
            <Space wrap size={[4, 4]}>
              {aliases.map((a) => (
                <Tag key={a.id}>
                  {a.alias}
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {' '}
                    · {a.source}/{a.review_state}
                  </Typography.Text>
                </Tag>
              ))}
            </Space>
          )}
        </Space>
      )}
    </Drawer>
  )
}

/* ── 相似问题（详情抽屉内；语义去重 similar_issues） ─────────────── */

function issueStatusLabel(status: string): string {
  return STATUS_LABELS[status as IssueStatus] ?? status
}

function issueAreaLabel(area: string | null): string | null {
  return area ? (AREA_LABELS[area as IssueArea] ?? area) : null
}

function SimilarityTag({
  similarity,
  sem,
  lex,
}: {
  similarity: number
  sem: number | null
  lex: number | null
}) {
  return (
    <Tooltip title={similarityBreakdown(sem, lex)}>
      <Tag
        color={similarityColor(similarity)}
        style={{ marginInlineEnd: 0, fontVariantNumeric: 'tabular-nums' }}
      >
        相似度 {formatSimilarity(similarity)}
      </Tag>
    </Tooltip>
  )
}

/** 问题摘要行：标题（点开详情）+ 状态 / 可见性 / 区域 / 支持数 */
function DedupIssueSummary({
  issue,
  onOpen,
  extra,
}: {
  issue: DedupIssueRef
  onOpen: (id: string) => void
  extra?: ReactNode
}) {
  const area = issueAreaLabel(issue.area)
  return (
    <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
      <Typography.Link
        onClick={(e) => {
          e.stopPropagation()
          onOpen(issue.id)
        }}
        style={{ fontSize: 13, wordBreak: 'break-word' }}
      >
        {issue.title || issue.id}
      </Typography.Link>
      <Space size={[4, 4]} wrap>
        <Tag
          color={statusColor(issue.status as IssueStatus)}
          style={{ marginInlineEnd: 0 }}
        >
          {issueStatusLabel(issue.status)}
        </Tag>
        <Tag
          color={pubColor(issue.publication_state)}
          style={{ marginInlineEnd: 0 }}
        >
          {PUB_LABELS[issue.publication_state] ?? issue.publication_state}
        </Tag>
        {area ? <Tag style={{ marginInlineEnd: 0 }}>{area}</Tag> : null}
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
        >
          支持 {issue.support_count}
        </Typography.Text>
        {extra}
      </Space>
    </Space>
  )
}

function SimilarIssuesPanel({
  items,
  disabled,
  onOpen,
  onMergeInto,
  onMergeHere,
}: {
  items: SimilarIssue[]
  disabled: boolean
  onOpen: (id: string) => void
  /** 当前问题 → 并入该相似问题 */
  onMergeInto: (item: SimilarIssue) => void
  /** 该相似问题 → 并入当前问题 */
  onMergeHere: (item: SimilarIssue) => void
}) {
  return (
    <div className="product-feedback-similar">
      <Divider titlePlacement="start" plain>
        相似问题 ({items.length})
      </Divider>
      {items.length === 0 ? (
        <Typography.Text type="secondary">
          暂无相似问题（相似度 ≥ 0.50）
        </Typography.Text>
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => (
            <List.Item>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <DedupIssueSummary
                  issue={item}
                  onOpen={onOpen}
                  extra={
                    <SimilarityTag
                      similarity={item.similarity}
                      sem={item.sem}
                      lex={item.lex}
                    />
                  }
                />
                <Space size={[8, 4]} wrap>
                  <Button
                    size="small"
                    danger
                    disabled={disabled}
                    onClick={() => onMergeInto(item)}
                  >
                    合并到此问题
                  </Button>
                  <Button
                    size="small"
                    disabled={disabled}
                    onClick={() => onMergeHere(item)}
                  >
                    把它合并进来
                  </Button>
                </Space>
              </Space>
            </List.Item>
          )}
        />
      )}
    </div>
  )
}

/* ── 疑似重复队列（admin.list_dedup_suggestions） ────────────────── */

const DEDUP_LIMIT_OPTIONS = [50, 100, 200]

function DedupQueue({
  viewSwitch,
  onOpenIssue,
}: {
  viewSwitch: ReactNode
  onOpenIssue: (id: string) => void
}) {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const merge = useProductIssueMerge()
  // null = 用后端默认下限（产品反馈弹窗阈值）
  const [minScoreInput, setMinScoreInput] = useState<number | null>(null)
  const [limit, setLimit] = useState(50)
  const minScore = useDebounced(minScoreInput, 500)

  const queryKey = [DEDUP_QUEUE_KEY, { minScore, limit }] as const
  const query = useQuery({
    queryKey,
    placeholderData: keepPreviousData,
    // 两两打分 1~3 s：不随窗口聚焦重跑，靠手动刷新 / 操作后失效
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_dedup_suggestions', {
          p_min_score: minScore,
          p_limit: limit,
        })
      if (error) throw error
      return parseDedupSuggestions(data)
    },
  })

  const updateCache = (fn: (list: DedupSuggestion[]) => DedupSuggestion[]) => {
    qc.setQueryData<DedupSuggestionsResult>(queryKey, (prev) =>
      prev ? { ...prev, suggestions: fn(prev.suggestions) } : prev,
    )
  }

  const dismissMutation = useMutation({
    mutationFn: async (s: DedupSuggestion) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('dismiss_dedup_suggestion', { p_a: s.a.id, p_b: s.b.id })
      if (error) throw error
      const r = data as { ok?: boolean; error?: string } | null
      if (!r?.ok) {
        throw new Error(
          r?.error === 'invalid_request'
            ? '问题不存在或参数无效，请刷新'
            : r?.error === 'forbidden'
              ? '无权限（需要管理员）'
              : (r?.error ?? '操作失败'),
        )
      }
      return s
    },
    onSuccess: (s) => {
      updateCache((list) => withoutPair(list, s.a.id, s.b.id))
      message.success('已标记为不是重复')
    },
    onError: (e: Error) => message.error(e.message || '操作失败'),
  })

  const mergePair = (s: DedupSuggestion, keep: 'a' | 'b') => {
    const target = keep === 'a' ? s.a : s.b
    const source = keep === 'a' ? s.b : s.a
    merge.confirmMerge(source, target, () =>
      // 源变 merged，自然退出队列：先本地移除，后台刷新再校正
      updateCache((list) => withoutIssue(list, source.id)),
    )
  }

  const suggestions = query.data?.suggestions ?? []
  const effectiveMin = query.data?.minScore ?? null
  const busy = merge.isPending || dismissMutation.isPending

  const keepLabel = (s: DedupSuggestion, side: 'a' | 'b') => {
    const recommended = suggestMergeKeep(s.a, s.b) === side
    return `保留 ${side.toUpperCase()}（${side === 'a' ? 'B' : 'A'} 并入 ${side.toUpperCase()}）${recommended ? ' · 推荐' : ''}`
  }

  const columns: ColumnsType<DedupSuggestion> = [
    {
      title: '问题 A',
      render: (_: unknown, s) => (
        <DedupIssueSummary issue={s.a} onOpen={onOpenIssue} />
      ),
    },
    {
      title: '问题 B',
      render: (_: unknown, s) => (
        <DedupIssueSummary issue={s.b} onOpen={onOpenIssue} />
      ),
    },
    {
      title: '相似度',
      width: 130,
      render: (_: unknown, s) => (
        <SimilarityTag similarity={s.similarity} sem={s.sem} lex={s.lex} />
      ),
    },
    {
      title: '操作',
      width: 200,
      fixed: 'right',
      render: (_: unknown, s) => (
        <Space size={4} wrap>
          <Dropdown
            trigger={['click']}
            disabled={busy}
            menu={{
              items: [
                { key: 'a', label: keepLabel(s, 'a') },
                { key: 'b', label: keepLabel(s, 'b') },
              ],
              onClick: ({ key }) => mergePair(s, key as 'a' | 'b'),
            }}
          >
            <Button size="small" danger disabled={busy}>
              合并 <DownOutlined />
            </Button>
          </Dropdown>
          <Popconfirm
            title="标记为不是重复？"
            description="这一对之后不再出现在队列里。"
            okText="不是重复"
            cancelText="取消"
            onConfirm={() =>
              // 失败已由 onError 提示；吞掉 rejection 免得 Popconfirm 报未处理
              dismissMutation.mutateAsync(s).catch(() => undefined)
            }
          >
            <Button size="small" disabled={busy}>
              不是重复
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Card
      title="产品反馈"
      extra={
        <Space size={8} wrap>
          {viewSwitch}
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => query.refetch()}
              loading={query.isFetching}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="未合并、未隐藏、未终态的问题两两比对，列出疑似重复。合并时选择保留哪一方；「不是重复」后该对不再出现。"
        />

        <Space size={[12, 8]} wrap align="center">
          <Space size={6}>
            <span style={{ fontSize: 13 }}>最低相似度</span>
            <InputNumber
              aria-label="最低相似度"
              min={0.5}
              max={1}
              step={0.01}
              precision={2}
              value={minScoreInput}
              placeholder={
                effectiveMin != null
                  ? `默认 ${formatSimilarity(effectiveMin)}`
                  : '默认'
              }
              onChange={(v) => setMinScoreInput(v ?? null)}
              style={{ width: 120 }}
            />
            {minScoreInput != null ? (
              <Button
                size="small"
                type="link"
                onClick={() => setMinScoreInput(null)}
              >
                恢复默认
              </Button>
            ) : null}
          </Space>
          <Select
            aria-label="最多显示"
            value={limit}
            onChange={setLimit}
            style={{ width: 120 }}
            options={DEDUP_LIMIT_OPTIONS.map((n) => ({
              value: n,
              label: `最多 ${n} 对`,
            }))}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {query.isFetching && !query.data
              ? '比对中…（约需 1–3 秒）'
              : `当前下限 ${formatSimilarity(effectiveMin)} · ${suggestions.length} 对${
                  suggestions.length >= limit
                    ? '（已达上限，可提高下限或条数）'
                    : ''
                }`}
          </Typography.Text>
        </Space>

        {query.isError ? (
          <Alert
            type="error"
            showIcon
            message={(query.error as Error).message || '加载失败'}
          />
        ) : null}

        <div className="admin-table-wrap">
          <Table<DedupSuggestion>
            rowKey={suggestionKey}
            size="middle"
            loading={query.isLoading}
            columns={columns}
            dataSource={suggestions}
            scroll={{ x: 960 }}
            pagination={false}
            locale={{ emptyText: '没有疑似重复的问题' }}
          />
        </div>

        <AdminMobileList
          rows={suggestions}
          rowKey={suggestionKey}
          loading={query.isLoading || query.isFetching}
          emptyContent={
            query.isLoading ? undefined : (
              <Typography.Text type="secondary">
                没有疑似重复的问题
              </Typography.Text>
            )
          }
          renderItem={(s) => (
            <article className="admin-mobile-record">
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    A
                  </Typography.Text>
                  <DedupIssueSummary issue={s.a} onOpen={onOpenIssue} />
                </div>
                <SimilarityTag
                  similarity={s.similarity}
                  sem={s.sem}
                  lex={s.lex}
                />
              </div>
              <div className="admin-mobile-record__title">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  B
                </Typography.Text>
                <DedupIssueSummary issue={s.b} onOpen={onOpenIssue} />
              </div>
              <AdminRecordActions
                maxVisible={2}
                actions={[
                  {
                    key: 'keep-a',
                    label:
                      suggestMergeKeep(s.a, s.b) === 'a'
                        ? '保留 A（推荐）'
                        : '保留 A',
                    primary: true,
                    danger: true,
                    disabled: busy,
                    onClick: () => mergePair(s, 'a'),
                  },
                  {
                    key: 'keep-b',
                    label:
                      suggestMergeKeep(s.a, s.b) === 'b'
                        ? '保留 B（推荐）'
                        : '保留 B',
                    primary: true,
                    danger: true,
                    disabled: busy,
                    onClick: () => mergePair(s, 'b'),
                  },
                  {
                    key: 'dismiss',
                    label: '不是重复',
                    disabled: busy,
                    onClick: () => dismissMutation.mutate(s),
                    confirm: {
                      title: '标记为不是重复？',
                      description: '这一对之后不再出现在队列里。',
                      okText: '不是重复',
                    },
                  },
                ]}
              />
            </article>
          )}
        />
      </Space>
    </Card>
  )
}

/* ── 来源上下文（environment.ref_*） ──────────────────────────── */

interface SourceLookup {
  animes: Map<number, string | null>
  episodes: Map<
    number,
    {
      anime_id: number
      episode_number: number
      kind: string
      title: string | null
    }
  >
}

/** 只读查番剧标题 / 集数，用于把 ref_* 渲染成可读文字；查不到就退回显示 id。 */
function useSourceLookup(refs: Array<EnvironmentRefs>) {
  const animeIds = [
    ...new Set(
      refs.map((r) => r.animeId).filter((v): v is number => v != null),
    ),
  ].sort((a, b) => a - b)
  const episodeIds = [
    ...new Set(
      refs.map((r) => r.episodeId).filter((v): v is number => v != null),
    ),
  ].sort((a, b) => a - b)

  const query = useQuery({
    queryKey: ['feedback_source_lookup', animeIds, episodeIds],
    enabled: animeIds.length > 0 || episodeIds.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<SourceLookup> => {
      const episodes = new Map<
        number,
        {
          anime_id: number
          episode_number: number
          kind: string
          title: string | null
        }
      >()
      if (episodeIds.length > 0) {
        const { data, error } = await supabase
          .from('episodes')
          .select('id, anime_id, episode_number, kind, title')
          .in('id', episodeIds)
        if (error) throw error
        for (const e of data ?? []) episodes.set(e.id, e)
      }
      const allAnimeIds = [
        ...new Set([
          ...animeIds,
          ...[...episodes.values()].map((e) => e.anime_id),
        ]),
      ]
      const animes = new Map<number, string | null>()
      if (allAnimeIds.length > 0) {
        const { data, error } = await supabase
          .from('animes')
          .select('id, title')
          .in('id', allAnimeIds)
        if (error) throw error
        for (const a of data ?? []) animes.set(a.id, a.title)
      }
      return { animes, episodes }
    },
  })
  return query.data
}

function SourceRefsLine({
  refs,
  lookup,
}: {
  refs: EnvironmentRefs
  lookup: SourceLookup | undefined
}) {
  const episode =
    refs.episodeId != null ? lookup?.episodes.get(refs.episodeId) : undefined
  // ref_anime_id 缺失时用剧集所属番剧兜底
  const animeId = refs.animeId ?? episode?.anime_id ?? null
  const animeTitle = animeId != null ? lookup?.animes.get(animeId) : undefined
  const playUrl =
    animeId != null && refs.episodeId != null
      ? (() => {
          const url = new URL(
            `/anime/${animeId}/play/${refs.episodeId}`,
            NEXTJS_BASE_URL.replace(/\/+$/, ''),
          )
          if (refs.playerCode) url.searchParams.set('source', refs.playerCode)
          return url.toString()
        })()
      : null

  return (
    <Space size={[6, 2]} wrap style={{ fontSize: 12 }}>
      <Tag color="blue" style={{ marginInlineEnd: 0 }}>
        来自播放页
      </Tag>
      {animeId != null ? (
        <Link
          to="/anime/$animeId/episodes"
          params={{ animeId: String(animeId) }}
        >
          {animeTitle ? `《${animeTitle}》` : `番剧 #${animeId}`}
        </Link>
      ) : null}
      {refs.episodeId != null ? (
        <Typography.Text style={{ fontSize: 12 }}>
          {episode
            ? `${formatEpisodeLabel(episode.kind, episode.episode_number)}${
                episode.title ? ` · ${episode.title}` : ''
              }`
            : `剧集 #${refs.episodeId}`}
        </Typography.Text>
      ) : null}
      {refs.playerCode ? (
        <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
          {refs.playerCode}
        </Tag>
      ) : null}
      {playUrl ? (
        <Typography.Link
          href={playUrl}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 12 }}
        >
          <ExportOutlined /> 播放页
        </Typography.Link>
      ) : null}
    </Space>
  )
}
